import { useState } from "react";
import { Download } from "lucide-react";
import {
  Button,
  Dropzone,
  SubmitButton,
  UploadFileRow,
  humanSize,
  useSubmittingMutation,
  useToast,
} from "@/design-system/components/ui";
import { formatDateInTz } from "@/design-system/lib/format";
import { WaitState } from "../components/WaitState";
import { SubmissionStatusCard } from "../components/SubmissionStatusCard";
import { ResponseBlockedHint, type PortalResponse } from "../components/ResponseStep";
import { portalPackagesApi } from "../api/packages";
import type { CollectionAiFeedbackResult, PortalItemDto, PortalUploadResult } from "../api/types";

// External Intake & Collection v1.0 PR 4 §2.3 — Upload fulfilment surface.
// Mobile-first: an explicit camera-capture input AND a regular file
// picker, so recipients on a phone can just photograph the document, and
// desktop recipients can still browse.
//
// PR 6 §2.2 — the upload no longer auto-submits. The recipient picks a
// file, taps "Check", we POST to /upload, render the advisory feedback
// inline, and only on a separate "Confirm submission" click do we POST to
// /staged-submissions/{id}/confirm. Feedback is coaching, not gating —
// the recipient can confirm even when issues are flagged.

interface UploadItemSurfaceProps {
  item: PortalItemDto;
  onUpdated: (next: PortalItemDto) => void;
  disabled?: boolean;
  /** Review loop — set while the item owes a response; the note is sent with
   *  the confirm, which stays disabled while it is blank. */
  response?: PortalResponse | null;
  /** Re-read the package after a form download (the server may have moved the
   *  item to In progress and stamped the download). */
  onReload?: () => void | Promise<void>;
}

const ACCEPTED = ".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,image/*";

export function UploadItemSurface({ item, onUpdated, disabled, response, onReload }: UploadItemSurfaceProps) {
  const [file, setFile] = useState<File | null>(null);
  const [staged, setStaged] = useState<PortalUploadResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const toast = useToast();

  const upload = useSubmittingMutation(
    async (f: File) => {
      const result = await portalPackagesApi.upload(item.id, f);
      setStaged(result);
    },
    {
      onError: (err) =>
        setError(err instanceof Error ? err.message : "Upload failed. Try again or use a smaller file."),
    },
  );

  const confirm = useSubmittingMutation(
    async (stagedSubmissionId: string) => {
      const next = await portalPackagesApi.confirmUpload(stagedSubmissionId, response?.note);
      onUpdated(next);
      setFile(null);
      setStaged(null);
      toast.success("Submitted ✓ — your file is now with your liaison for review.");
    },
    {
      onError: (err) =>
        setError(err instanceof Error ? err.message : "Could not finalize. Try again."),
    },
  );

  const busy = upload.pending || confirm.pending;

  function handleUpload() {
    if (!file) return;
    setError(null);
    void upload.submit(file);
  }

  function handleConfirm() {
    if (!staged || response?.blockedReason) return;
    setError(null);
    void confirm.submit(staged.stagedSubmissionId);
  }

  // Upload templates (U6b) — the "Form to complete" card sits between the
  // Instructions card (rendered by ItemPage above this surface) and the dropzone.
  const formCard = item.formAttachmentFileName ? (
    <FormToCompleteCard item={item} onDownloaded={onReload} />
  ) : null;

  if (disabled && item.status === "SubmittedForReview") {
    return (
      <div className="flex flex-col gap-space-4">
        {formCard}
        <SubmissionStatusCard tone="review" title="Submitted for review" data-testid="upload-under-review">
          Your file is being reviewed. We'll let you know if anything else is needed.
        </SubmissionStatusCard>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-space-4">
      {formCard}
      {item.status === "InProgress" && !staged && !busy && (
        <SubmissionStatusCard tone="progress" title="In progress" data-testid="upload-in-progress">
          You've started this request — upload your file when you're ready, and we'll take a quick
          look before you submit.
        </SubmissionStatusCard>
      )}
      {!staged && (
        <div className="flex flex-col gap-space-3">
          <Dropzone
            accept={ACCEPTED}
            disabled={disabled || busy}
            hint="PDF, Word, Excel, or an image"
            onFiles={(files) => setFile(files[0] ?? null)}
            inputTestId="upload-file"
          />

          <label className="block">
            <span className="block text-small font-medium text-onyx mb-space-2">
              On a phone? Take a photo of the document
            </span>
            <input
              type="file"
              accept="image/*"
              capture="environment"
              disabled={disabled || busy}
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              className="block w-full text-small"
              data-testid="upload-camera"
            />
          </label>
        </div>
      )}

      {file && !staged && (
        <UploadFileRow
          name={file.name}
          sizeBytes={file.size}
          state={upload.pending ? "scanning" : "ready"}
          message={upload.pending ? "Checking…" : "Ready to check"}
          onRemove={!busy ? () => setFile(null) : undefined}
        />
      )}

      {upload.pending && (
        <WaitState
          label="Checking your file…"
          message="Our assistant is taking a quick look at your document. This can take up to a minute — please don't close this tab."
        />
      )}

      {confirm.pending && (
        <SubmissionStatusCard tone="progress" title="Submitting…" data-testid="upload-submitting">
          We're finalizing your submission. This can take a moment — please don't close this tab.
        </SubmissionStatusCard>
      )}

      {staged && <FeedbackPanel feedback={staged.feedback} fileName={staged.originalFileName} />}

      {error && <p className="text-small text-status-error m-0" role="alert">{error}</p>}

      {!disabled && !staged && (
        <SubmitButton
          onClick={handleUpload}
          pending={upload.pending}
          pendingLabel="Checking…"
          disabled={!file || busy}
        >
          Check before submitting
        </SubmitButton>
      )}

      {!disabled && staged && (
        <div className="flex flex-col gap-space-2 sm:flex-row">
          <SubmitButton
            onClick={handleConfirm}
            pending={confirm.pending}
            pendingLabel="Submitting…"
            disabled={busy || !!response?.blockedReason}
          >
            Confirm submission
          </SubmitButton>
          <Button
            variant="secondary"
            onClick={() => { setStaged(null); setFile(null); }}
            disabled={busy}
          >
            Upload a different file
          </Button>
        </div>
      )}
      {!disabled && staged && <ResponseBlockedHint response={response} />}
    </div>
  );
}

/** Upload templates (U6b) — the document the recipient downloads, completes or
 *  signs, and uploads back. Streamed through the scoped portal endpoint; the
 *  first download moves a Pending item to In progress server-side, so the
 *  package is re-read afterwards. */
function FormToCompleteCard({
  item,
  onDownloaded,
}: {
  item: PortalItemDto;
  onDownloaded?: () => void | Promise<void>;
}) {
  const toast = useToast();
  const [downloading, setDownloading] = useState(false);
  const fileName = item.formAttachmentFileName ?? "";

  async function download() {
    setDownloading(true);
    try {
      const blob = await portalPackagesApi.downloadFormAttachment(item.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      await onDownloaded?.();
    } catch {
      toast.error("Could not download the form. Try again.");
    } finally {
      setDownloading(false);
    }
  }

  return (
    <section
      className="p-space-4 bg-white-pure border border-border-light rounded-lg flex flex-col gap-space-3"
      aria-labelledby={`form-to-complete-${item.id}`}
      data-testid="form-to-complete-card"
    >
      <h2 id={`form-to-complete-${item.id}`} className="text-body font-display text-onyx m-0">
        Form to complete
      </h2>
      <div className="flex flex-col gap-space-1">
        <p className="text-small font-semibold text-onyx m-0 break-words" data-testid="form-to-complete-file">
          {item.formAttachmentSizeBytes != null
            ? `${fileName} · ${humanSize(item.formAttachmentSizeBytes)}`
            : fileName}
        </p>
        <p className="text-caption text-gray-mid m-0">
          Download this form, complete and sign it, then upload the signed copy below. You can leave
          this page and come back to upload it later.
        </p>
      </div>
      <div className="flex flex-col items-start gap-space-1">
        <Button
          variant="secondary"
          onClick={() => void download()}
          disabled={downloading}
          data-testid="form-to-complete-download"
        >
          <Download size={14} className="mr-space-1" aria-hidden />
          {item.formAttachmentButtonLabel || "Download form"}
        </Button>
        {item.formDownloadedAt && (
          <p className="text-caption text-gray-mid m-0" data-testid="form-to-complete-downloaded">
            Downloaded {formatDateInTz(item.formDownloadedAt, null)}.
          </p>
        )}
      </div>
    </section>
  );
}

function FeedbackPanel({
  feedback,
  fileName,
}: { feedback: CollectionAiFeedbackResult; fileName: string }) {
  const tone =
    feedback.verdict === "LooksGood" ? "bg-status-success/10 border-status-success" :
    feedback.verdict === "NeedsAttention" ? "bg-status-warning/10 border-status-warning" :
    "bg-white-pure border-border-light";

  const headline =
    feedback.verdict === "LooksGood" ? "Looks good!" :
    feedback.verdict === "NeedsAttention" ? "A couple of things to check" :
    "We couldn't preview this — submit when you're ready";

  return (
    <div className={`p-space-3 border rounded-md ${tone}`} data-testid="upload-feedback">
      <div className="flex items-center gap-space-2">
        <span
          aria-hidden="true"
          className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-gold/30 text-onyx text-caption font-display font-bold"
        >
          P
        </span>
        <span className="text-caption text-gray-mid uppercase tracking-wide">
          Phoenix Assistant
        </span>
      </div>
      <p className="text-small font-medium text-onyx m-0 mt-space-2">{headline}</p>
      <p className="text-caption text-gray-mid m-0 mt-space-1">
        {fileName}
      </p>
      {feedback.summary && (
        <p className="text-small text-onyx m-0 mt-space-2">{feedback.summary}</p>
      )}
      {feedback.issues.length > 0 && (
        <ul className="text-small text-onyx m-0 mt-space-2 pl-space-4 list-disc">
          {feedback.issues.map((issue, idx) => (
            <li key={idx} className="mt-space-1">
              <strong>{issue.title}</strong>
              {issue.suggestion ? ` — ${issue.suggestion}` : null}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
